'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { REFERRAL_QUERY_KEYS } from '../../infrastructure/referral/utils/keys';
import { REFERRAL_QUERIES } from '../../infrastructure/referral/utils/queries';
import type { ReferralMemberState } from '../../model/referral/referral';

function useReferralCountrySync({
  customerId,
  country,
  enabled,
  member,
}: {
  customerId: string;
  country: string;
  enabled: boolean;
  member: ReferralMemberState | undefined;
}) {
  const queryClient = useQueryClient();
  const isSavingCountry = useIsMutating({ mutationKey: REFERRAL_QUERY_KEYS.country(customerId) }) > 0;
  const isEnsuring = useIsMutating({ mutationKey: REFERRAL_QUERY_KEYS.ensure(customerId) }) > 0;
  const hasCountryMismatch = Boolean(member && member.status !== 'unissued' && member.country !== country);
  const update = useMutation(REFERRAL_QUERIES.updateCountry(queryClient, customerId));
  const attemptedCountry = useRef('');
  const selectionKey = `${customerId}:${country}`;
  useEffect(() => {
    attemptedCountry.current = '';
  }, [selectionKey]);
  useEffect(() => {
    if (
      !enabled ||
      !customerId ||
      !hasCountryMismatch ||
      isEnsuring ||
      attemptedCountry.current === selectionKey ||
      queryClient.isMutating({ mutationKey: REFERRAL_QUERY_KEYS.country(customerId) })
    ) {
      return;
    }
    attemptedCountry.current = selectionKey;
    update.mutate({ country });
  }, [
    enabled,
    customerId,
    country,
    selectionKey,
    hasCountryMismatch,
    queryClient,
    isSavingCountry,
    isEnsuring,
    update.mutate,
    update.status,
  ]);
  const isError = hasCountryMismatch && update.isError && attemptedCountry.current === selectionKey;
  return {
    isPending: isSavingCountry || (hasCountryMismatch && !isError),
    isError,
    reset: () => {
      attemptedCountry.current = '';
      update.reset();
    },
  };
}

export const useReferralService = () => {
  // Queries are conditional: public configuration gates rollout, and membership requires a resolved session.
  const useConfig = (country: string, enabled = true) => useQuery({ ...REFERRAL_QUERIES.config(country), enabled });
  const useReferralMember = (customerId: string, country: string, enabled: boolean) => {
    const countryKey = REFERRAL_QUERY_KEYS.country(customerId);
    const isSavingCountry = useIsMutating({ mutationKey: countryKey }) > 0;
    return useQuery({
      ...REFERRAL_QUERIES.member(customerId, country),
      enabled: enabled && Boolean(customerId) && !isSavingCountry,
    });
  };
  const useEnsureReferralMember = (customerId: string, country: string) => {
    const queryClient = useQueryClient();
    const mutation = useMutation(REFERRAL_QUERIES.ensureMember(queryClient, customerId, country));
    const { mutateAsync } = mutation;
    // A changed mutation key resets after render; hide the previous selection's state immediately.
    const isCurrent = mutation.context?.customerId === customerId && mutation.variables?.country === country;
    const status = isCurrent ? mutation.status : 'idle';
    const ensureMember = useCallback(() => mutateAsync({ country }), [country, mutateAsync]);
    return {
      mutateAsync: ensureMember,
      status,
      error: isCurrent ? mutation.error : null,
      isError: status === 'error',
      isPending: status === 'pending',
    };
  };
  const useStats = (customerId: string, currency: string, enabled: boolean) =>
    useQuery({ ...REFERRAL_QUERIES.stats(customerId, currency), enabled: enabled && Boolean(customerId) });
  const useRecordArrival = () => useMutation(REFERRAL_QUERIES.arrival());
  const useSyncIdentity = () => useMutation(REFERRAL_QUERIES.syncIdentity());
  const useUpdateCountry = (customerId: string, onSaved?: (country: string) => void | Promise<void>) => {
    const queryClient = useQueryClient();
    const options = REFERRAL_QUERIES.updateCountry(queryClient, customerId);
    return useMutation({
      ...options,
      onSuccess: async (...args) => {
        await onSaved?.(args[1].country);
        await options.onSuccess?.(...args);
      },
    });
  };
  return {
    useConfig,
    useReferralMember,
    useEnsureReferralMember,
    useReferralCountrySync,
    useStats,
    useRecordArrival,
    useSyncIdentity,
    useUpdateCountry,
  };
};
